/**
 * FIXTURE DATA — EVERY VALUE IN THIS FILE IS FAKE.
 *
 * No real secret, account, name or domain appears here (studio-designer D7). Rules, enforced by
 * tests/vault-pwa/fixtures.test.ts:
 * - every password, key, card number and one-time-code seed starts with "FAKE";
 * - every domain is under example.com, example.org, example.net or .example / .test (RFC 2606);
 * - wallet ids never match a real Vowed wallet name (personal, coi, vk, family, gl, mooring);
 * - nothing matches a real credential shape (AWS key id, GitHub token, Stripe live key, PEM).
 *
 * Three fake wallets: `home`, `work`, `shared`. `home` also holds a generated bulk set, so the
 * Items list has the scale of an imported wallet (~600 rows, 01-references §6 S3).
 */
import type { CategoryId, ItemField, VaultItem, Wallet } from "../contract.js";

export const FIXTURE_WALLETS: Wallet[] = [
  { id: "home", name: "Home" },
  { id: "work", name: "Work" },
  { id: "shared", name: "Shared" },
];

/** Fixed reference instant so fixture dates and screenshots are deterministic. */
export const FIXTURE_EPOCH = Date.parse("2026-09-24T09:00:00-05:00");

const day = 86_400_000;
const iso = (msAgo: number) => new Date(FIXTURE_EPOCH - msAgo).toISOString();

interface Spec {
  slug: string;
  wallet: string;
  title: string;
  category: CategoryId;
  user?: string;
  password?: string;
  url?: string;
  otp?: boolean;
  extra?: [string, string, boolean][]; // label, value, sensitive
  note?: string;
  tags?: Record<string, string>;
  favorite?: boolean;
  archived?: boolean;
  deleted?: boolean;
  modifiedDaysAgo: number;
  usedDaysAgo?: number;
  useCount?: number;
}

function build(s: Spec): VaultItem {
  const fields: ItemField[] = [];
  if (s.user) fields.push({ id: "username", kind: "username", label: "username", value: s.user, sensitive: false });
  if (s.password) fields.push({ id: "password", kind: "password", label: "password", value: s.password, sensitive: true });
  if (s.otp) {
    fields.push({
      id: "otp",
      kind: "otp",
      label: "one-time code",
      value: `FAKE-SEED-${s.slug}`,
      sensitive: false,
      otp: { period: 30, digits: 6 },
    });
  }
  if (s.url) fields.push({ id: "website", kind: "url", label: "website", value: s.url, sensitive: false });
  for (const [i, [label, value, sensitive]] of (s.extra ?? []).entries()) {
    fields.push({ id: `f${i}`, kind: "text", label, value, sensitive });
  }
  if (s.note) fields.push({ id: "notes", kind: "note", label: "notes", value: s.note, sensitive: false });
  const tags: Record<string, string> = { type: s.category, ...(s.tags ?? {}) };
  if (s.url) tags.domain = new URL(s.url).hostname;
  return {
    id: `${s.wallet}/${s.slug}`,
    wallet: s.wallet,
    title: s.title,
    category: s.category,
    tags,
    fields,
    favorite: s.favorite ?? false,
    archived: s.archived ?? false,
    deleted: s.deleted ?? false,
    createdAt: iso((s.modifiedDaysAgo + 120) * day),
    modifiedAt: iso(s.modifiedDaysAgo * day),
    lastUsedAt: s.usedDaysAgo === undefined ? undefined : iso(s.usedDaysAgo * day),
    useCount: s.useCount ?? 0,
  };
}

const HAND: Spec[] = [
  // ---- home
  { slug: "northwind-bank", wallet: "home", title: "Northwind Bank", category: "login", user: "jordan.avery@example.com", password: "FAKE-rT9#vLq2-Northwind", url: "https://bank.northwind.example", otp: true, favorite: true, modifiedDaysAgo: 40, usedDaysAgo: 0.1, useCount: 88 },
  { slug: "contoso-mail", wallet: "home", title: "Contoso Mail", category: "login", user: "jordan.avery@example.com", password: "FAKE-mail-Qz81!pw", url: "https://mail.contoso.example", otp: true, favorite: true, modifiedDaysAgo: 12, usedDaysAgo: 0.3, useCount: 140 },
  { slug: "fabrikam-air", wallet: "home", title: "Fabrikam Air", category: "login", user: "JAVERY-FF-000000", password: "FAKE-fly-7713-Hx", url: "https://www.fabrikam-air.example", tags: { program: "frequent flyer" }, modifiedDaysAgo: 90, usedDaysAgo: 6, useCount: 9 },
  { slug: "tailspin-streaming", wallet: "home", title: "Tailspin Streaming", category: "login", user: "jordan.avery@example.com", password: "FAKE-stream-Kk2m9", url: "https://tailspin.example", modifiedDaysAgo: 200, usedDaysAgo: 20, useCount: 4 },
  { slug: "wingtip-utilities", wallet: "home", title: "Wingtip Utilities", category: "login", user: "acct-0000-1111", password: "FAKE-util-Pw!2026", url: "https://pay.wingtip-utilities.example", modifiedDaysAgo: 30, usedDaysAgo: 29, useCount: 12 },
  { slug: "adventure-works-store", wallet: "home", title: "Adventure Works Store", category: "login", user: "javery", password: "FAKE-shop-9vB!", url: "https://store.adventure-works.example", modifiedDaysAgo: 15, usedDaysAgo: 2, useCount: 21 },
  { slug: "home-wifi", wallet: "home", title: "Home Wi-Fi", category: "password", password: "FAKE-wifi-correct-horse", extra: [["network name", "FAKE-NETWORK-5G", false]], favorite: true, modifiedDaysAgo: 400, usedDaysAgo: 3, useCount: 7 },
  { slug: "visa-everyday", wallet: "home", title: "Everyday Visa", category: "credit-card", extra: [["cardholder", "Jordan Avery", false], ["number", "FAKE 4000 0000 0000 0002", true], ["expiry", "04/29", false], ["verification number", "FAKE-123", true]], modifiedDaysAgo: 60, usedDaysAgo: 1, useCount: 33 },
  { slug: "passport", wallet: "home", title: "Passport", category: "identity", extra: [["full name", "Jordan Avery", false], ["number", "FAKE-P0000000", true], ["issued by", "Republic of Example", false], ["expires", "2031-05-01", false]], modifiedDaysAgo: 700, useCount: 1 },
  { slug: "garage-code", wallet: "home", title: "Garage Keypad", category: "secure-note", note: "FAKE: keypad code is 0000 then the star key. Spare remote is in the kitchen drawer.", modifiedDaysAgo: 300, usedDaysAgo: 45, useCount: 2 },
  { slug: "old-forum", wallet: "home", title: "Old Hobby Forum", category: "login", user: "javery99", password: "FAKE-forum-2011", url: "https://forum.example.org", archived: true, modifiedDaysAgo: 1500, useCount: 0 },
  { slug: "deleted-game", wallet: "home", title: "Retired Game Account", category: "login", user: "javery", password: "FAKE-game-00", url: "https://games.example.net", deleted: true, modifiedDaysAgo: 5, useCount: 0 },

  // ---- work
  { slug: "litware-sso", wallet: "work", title: "Litware SSO", category: "login", user: "j.avery@litware.example", password: "FAKE-sso-L1tw4re!", url: "https://sso.litware.example", otp: true, favorite: true, modifiedDaysAgo: 8, usedDaysAgo: 0.05, useCount: 310 },
  { slug: "litware-vpn", wallet: "work", title: "Litware VPN", category: "login", user: "javery", password: "FAKE-vpn-Tunnel#9", url: "https://vpn.litware.example", otp: true, modifiedDaysAgo: 8, usedDaysAgo: 1, useCount: 150 },
  { slug: "proseware-api", wallet: "work", title: "Proseware API (staging)", category: "api-credential", extra: [["key id", "FAKE-KEYID-STAGING-01", false], ["secret", "FAKE-sk-staging-6f2c0d9e", true], ["endpoint", "https://api.proseware.example/v2", false]], tags: { env: "staging", provider: "proseware" }, modifiedDaysAgo: 20, usedDaysAgo: 4, useCount: 26 },
  { slug: "proseware-api-prod", wallet: "work", title: "Proseware API (production)", category: "api-credential", extra: [["key id", "FAKE-KEYID-PROD-01", false], ["secret", "FAKE-sk-prod-91ab77e0", true], ["endpoint", "https://api.proseware.example/v2", false]], tags: { env: "production", provider: "proseware" }, modifiedDaysAgo: 20, usedDaysAgo: 11, useCount: 8 },
  { slug: "orders-db", wallet: "work", title: "Orders DB (read replica)", category: "database", user: "readonly_app", password: "FAKE-db-R3plica!", extra: [["host", "orders-ro.db.example", false], ["port", "5432", false], ["database", "orders", false]], tags: { engine: "postgres", env: "production" }, modifiedDaysAgo: 75, usedDaysAgo: 9, useCount: 14 },
  { slug: "build-server", wallet: "work", title: "Build Server", category: "server", user: "deploy", password: "FAKE-ssh-B0x!", extra: [["address", "build-01.ci.example", false]], tags: { role: "ci" }, modifiedDaysAgo: 110, usedDaysAgo: 18, useCount: 5 },
  { slug: "consolidated-payroll", wallet: "work", title: "Consolidated Payroll", category: "login", user: "j.avery@litware.example", password: "FAKE-pay-Rl2026", url: "https://payroll.consolidated.example", modifiedDaysAgo: 33, usedDaysAgo: 14, useCount: 12 },
  { slug: "blue-yonder-travel", wallet: "work", title: "Blue Yonder Travel", category: "login", user: "j.avery@litware.example", password: "FAKE-trip-8Hn", url: "https://book.blueyonder.example", modifiedDaysAgo: 140, usedDaysAgo: 60, useCount: 3 },
  { slug: "office-alarm", wallet: "work", title: "Office Alarm Panel", category: "secure-note", note: "FAKE: arm code 1234, disarm within 30 seconds. Monitoring company line: 555-0100.", modifiedDaysAgo: 250, useCount: 1 },
  { slug: "corp-card", wallet: "work", title: "Corporate Card", category: "credit-card", extra: [["cardholder", "Jordan Avery", false], ["number", "FAKE 5555 0000 0000 4444", true], ["expiry", "11/28", false], ["verification number", "FAKE-456", true]], modifiedDaysAgo: 45, usedDaysAgo: 7, useCount: 19 },
  { slug: "legacy-crm", wallet: "work", title: "Legacy CRM", category: "login", user: "javery", password: "FAKE-crm-2019", url: "https://crm-old.litware.example", archived: true, modifiedDaysAgo: 900, useCount: 0 },

  // ---- shared
  { slug: "family-streaming", wallet: "shared", title: "Tailspin Streaming (shared)", category: "login", user: "household@example.com", password: "FAKE-share-Str3am", url: "https://tailspin.example", favorite: true, modifiedDaysAgo: 70, usedDaysAgo: 2, useCount: 40 },
  { slug: "grocery-delivery", wallet: "shared", title: "Grocery Delivery", category: "login", user: "household@example.com", password: "FAKE-groc-Bag5!", url: "https://grocer.example", modifiedDaysAgo: 22, usedDaysAgo: 5, useCount: 27 },
  { slug: "school-portal", wallet: "shared", title: "School Portal", category: "login", user: "parent-0000", password: "FAKE-school-Ab!1", url: "https://portal.school.example", modifiedDaysAgo: 18, usedDaysAgo: 8, useCount: 16 },
  { slug: "pediatrics", wallet: "shared", title: "Pediatrics Patient Portal", category: "login", user: "household@example.com", password: "FAKE-health-Kid2", url: "https://mychart.clinic.example", modifiedDaysAgo: 95, usedDaysAgo: 31, useCount: 6 },
  { slug: "insurance", wallet: "shared", title: "Home Insurance", category: "secure-note", note: "FAKE: policy HO-0000-0000, agent line 555-0199, renewal each March.", modifiedDaysAgo: 180, useCount: 2 },
  { slug: "guest-wifi", wallet: "shared", title: "Guest Wi-Fi", category: "password", password: "FAKE-guest-Welcome", extra: [["network name", "FAKE-GUEST", false]], modifiedDaysAgo: 365, usedDaysAgo: 12, useCount: 10 },
  { slug: "utility-water", wallet: "shared", title: "City Water", category: "login", user: "acct-0000-2222", password: "FAKE-water-H2o!", url: "https://pay.water.example", modifiedDaysAgo: 55, usedDaysAgo: 28, useCount: 8 },
];

// ---- generated bulk set: a deterministic "imported wallet" so the list has real scale.
const WORDS = [
  "Alpine", "Beacon", "Cedar", "Delta", "Ember", "Falcon", "Granite", "Harbor", "Indigo", "Juniper",
  "Kestrel", "Lumen", "Maple", "Nimbus", "Orchid", "Pioneer", "Quartz", "Raven", "Summit", "Timber",
  "Umber", "Vertex", "Willow", "Xenon", "Yarrow", "Zephyr",
];
const KINDS = ["Books", "Cloud", "Fitness", "Games", "Market", "News", "Photos", "Pay", "Rides", "Tickets", "Tools", "Travel", "Studio", "Garden", "Pets", "Music", "Coffee", "Energy", "Health", "Maps", "Notes", "Parking", "Recipes"];

function bulk(): Spec[] {
  const out: Spec[] = [];
  let n = 0;
  for (const w of WORDS) {
    for (const k of KINDS) {
      n++;
      const slug = `${w}-${k}`.toLowerCase();
      out.push({
        slug,
        wallet: "home",
        title: `${w} ${k}`,
        category: "login",
        user: `jordan.avery+${slug}@example.com`,
        password: `FAKE-${slug}-${(n * 7919) % 100000}`,
        url: `https://${slug}.example`,
        modifiedDaysAgo: 30 + ((n * 37) % 900),
        usedDaysAgo: n % 5 === 0 ? 15 + (n % 60) : undefined,
        useCount: n % 5 === 0 ? 1 + (n % 7) : 0,
      });
    }
  }
  return out;
}

export function fixtureItems(): VaultItem[] {
  return [...HAND, ...bulk()].map(build);
}

/** Fixture Home pins, as `${itemId}#${fieldId}` references (UI preference, not vault data). */
export const FIXTURE_PINNED_FIELDS = [
  "work/litware-sso#otp",
  "home/home-wifi#password",
  "work/orders-db#password",
];
